/**
 * The wire form of an `each()` row key inside its `<!--c:row:KEY-->` marker.
 *
 * A comment ends at `-->` and also at `--!>`, and a key is author data, so it
 * is written in a closed alphabet: letters, digits, `:`, `_` and `-` pass
 * through, and every other UTF-8 byte is written as `%XX`. No encoded key can
 * contain `>`, `!` or `<`, so none can close the comment or open another.
 */
import { InstructionError } from './values';

const PASSTHROUGH = /^[A-Za-z0-9:_-]$/;
const ENCODED = /^(?:[A-Za-z0-9:_-]|%[0-9A-F]{2})*$/;
const encoder = new TextEncoder();

function hexByte(byte: number): string {
  return `%${byte.toString(16).toUpperCase().padStart(2, '0')}`;
}

/** Encodes a row key for its comment marker; a key that is not well-formed UTF-16 is refused, since it could not round-trip. */
export function encodeRowKey(key: string): string {
  let out = '';
  for (const char of key) {
    // Iterating by code point leaves a lone surrogate on its own; it has no UTF-8 form to round-trip.
    const code = char.codePointAt(0) ?? 0;
    if (code >= 0xd800 && code <= 0xdfff) throw new InstructionError('each() keys must be well-formed text');
    if (PASSTHROUGH.test(char)) {
      out += char;
      continue;
    }
    for (const byte of encoder.encode(char)) out += hexByte(byte);
  }
  return out;
}

/** The key a marker carries, or `null` when the marker text is not an encoded key. */
export function decodeRowKey(encoded: string): string | null {
  if (!ENCODED.test(encoded)) return null;
  try {
    return decodeURIComponent(encoded);
  } catch {
    return null;
  }
}
